import {Box, Button, Divider, InputAdornment, Stack, TextField, ToggleButtonGroup, Typography} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import {Navigate, useLocation, useNavigate, useParams} from "react-router";
import {appSession} from "../../../TR3AD.ts";
import WidgetHeader from "../../../widgets/WidgetHeader.tsx";
import {appStyles} from "../../../AppStyles.ts";
import {useMemo, useState} from "react";
import {
    StyledAlphaFilterToggleButton,
    StyledInputBase,
    StyledSearch,
    StyledSearchIconWrapper
} from "../../AppStyledComponents.tsx";
import type {SessionIntelProfile} from "../../../sdo/SessionIntelProfile.ts";

// Lightweight mirror of desktop com.odi.api.ODIPointer — enough for card display.
interface SamplePointer {
    display: string;
    matchHash: string;
    typeKey: string;
    definitionKey: string;
}

// Sample display names — spread across A–Z plus a few non-alpha entries
// TODO: Replace with NetworkRegistryManager.readRegistry(intelType, definitionKey, selectKey)
const SAMPLE_DISPLAYS = [
    "Abrams, Jordan", "Anderson, Priya", "Atlas Logistics", "Ayers, Marcus",
    "Baker, Helena", "Brooks, Daniel", "Bowen, Sasha",
    "Carter, Noemi", "Chen, Wei", "Clarke, Terrence",
    "Davidson, Olivia", "Diaz, Rafael", "Donovan, Hugh",
    "Elkins, Ines", "Evans, Kofi",
    "Ferguson, Mira", "Foster, Dominic",
    "Gallagher, Nina", "Grant, Tobias",
    "Hamilton, Rowan", "Henderson, Yelena",
    "Ito, Kenji",
    "Jackson, Amara", "Jameson, Lucas",
    "Khan, Reza", "Kline, Anders",
    "Larsen, Freya", "Lynch, Corbin",
    "Martinez, Paola", "Mitchell, Devon",
    "Nolan, Brigid", "Novak, Stefan",
    "O'Brien, Siobhan", "Osborne, Mateo",
    "Pearson, Isla", "Phillips, Harlan",
    "Quincey, Mara", "Quinn, Thomas",
    "Reyes, Ximena", "Rivera, Bruno",
    "Santos, Paulo", "Saunders, Delia",
    "Torres, Lila", "Tucker, Everett",
    "Underwood, Piper",
    "Valentine, Arlo", "Vasquez, Esme",
    "Walsh, Finn", "Wilson, Marisol",
    "Xu, Ning",
    "Yamamoto, Haruki",
    "Zhang, Mei",
    "!Redacted-01", "#Unnamed-07", "1st Precinct Evidence Bin",
];

function buildSamplePointers(typeKey: string, definitionKey: string): SamplePointer[] {
    return SAMPLE_DISPLAYS.map((display, idx) => ({
        display,
        matchHash: `sample-${definitionKey.toLowerCase()}-${String(idx + 1).padStart(3, "0")}`,
        typeKey,
        definitionKey,
    }));
}

function titleCase(raw: string): string {
    if (!raw) return "";
    return raw
        .split(/[._-]+/)
        .map((part) => part.length === 0 ? "" : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(" ")
        .trim();
}

function firstCharBucket(display: string): string {
    const c = display.trim().charAt(0).toUpperCase();
    if (c >= "A" && c <= "Z") return c;
    return "!#1";
}

type LocationStateShape = { profile?: SessionIntelProfile & { type?: string } };

function UXIntelManager() {
    const { networkUID, typeKey, defKey } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const state = (location.state ?? null) as LocationStateShape | null;

    // Friendly display name — prefer profile passed via router state, fall back to title-cased URL slug.
    // Matches desktop intelDefinition.getPlural() behavior.
    const friendlyName = useMemo(() => {
        if (state?.profile?.name) return state.profile.name;
        return titleCase(String(defKey ?? ""));
    }, [state, defKey]);

    const friendlyType = useMemo(() => titleCase(String(typeKey ?? "")), [typeKey]);

    const LEFT_1 = "ABCDEFGHIJKLM".split("");
    const LEFT_2 = "NOPQRSTUVWXYZ".split("");
    const SPAN = "!#1";

    const [activeKey, setActiveKey] = useState<string>("A");
    const [datasetSearch, setDatasetSearch] = useState<string>("");
    const [resultsFilter, setResultsFilter] = useState<string>("");

    const pointers = useMemo(
        () => buildSamplePointers(String(typeKey ?? "entity"), String(defKey ?? "unknown")),
        [typeKey, defKey]
    );

    // Per-letter counts across the full dataset (A..Z + !#1)
    const counts = useMemo(() => {
        const map: Record<string, number> = {};
        LEFT_1.concat(LEFT_2).forEach((l) => { map[l] = 0; });
        map[SPAN] = 0;
        pointers.forEach((p) => {
            const bucket = firstCharBucket(p.display);
            map[bucket] = (map[bucket] ?? 0) + 1;
        });
        return map;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pointers]);

    // Dataset-wide search first; otherwise bucket by active letter. Then apply per-results filter.
    const visible = useMemo(() => {
        const datasetQ = datasetSearch.trim().toLowerCase();
        const base = datasetQ.length > 0
            ? pointers.filter((p) => p.display.toLowerCase().includes(datasetQ))
            : pointers.filter((p) => firstCharBucket(p.display) === activeKey);

        const resultQ = resultsFilter.trim().toLowerCase();
        return resultQ.length > 0
            ? base.filter((p) => p.display.toLowerCase().includes(resultQ))
            : base;
    }, [pointers, datasetSearch, activeKey, resultsFilter]);

    const filteredCount = visible.length;

    return (
        <Stack direction="column" width="100%" minHeight="100vh">
            {appSession.activeSession() === false ? (
                <Navigate to="/" />
            ) : (
                <Stack direction="column" spacing={1} width="99%" >
                    <WidgetHeader
                        title={`Intelligence: ${friendlyName}`}
                        breadcrumbs={[
                            {
                                display: "Networks",
                                linkTo: `/networks/`,
                                activeView: false
                            },
                            {
                                display: String(networkUID),
                                linkTo: `/networks/${networkUID}`,
                                activeView: false
                            },
                            {
                                display: "Intelligence",
                                linkTo: `/networks/${networkUID}`,
                                activeView: false
                            },
                            {
                                display: friendlyType,
                                linkTo: `/networks/${networkUID}`,
                                activeView: false
                            },
                            {
                                display: friendlyName,
                                linkTo: "",
                                activeView: true
                            }]}
                    />
                    <Stack direction="row" spacing={2} width="100%" alignItems="stretch">
                        <Stack sx={appStyles.alphaNumberFilterLeftRail}>
                            <Stack direction="column" alignItems="center" spacing={0}>
                                <StyledSearch>
                                    <StyledSearchIconWrapper>
                                        <img src="/images/search_white.svg" width={16} />
                                    </StyledSearchIconWrapper>
                                    <StyledInputBase
                                        placeholder="Search"
                                        value={datasetSearch}
                                        onChange={(e) => setDatasetSearch(e.target.value)}
                                        inputProps={{ "aria-label": "search" }}
                                    />
                                </StyledSearch>
                                <Stack sx={appStyles.alphaNumberDatasetSearchDivider}></Stack>
                                {/* A–Z rails */}
                                <Stack direction="row" spacing={1} sx={appStyles.alphaNumberFilterAlphaRailRow}>
                                    {/* A–M */}
                                    <ToggleButtonGroup
                                        orientation="vertical"
                                        value={activeKey}
                                        exclusive
                                        onChange={(_, v) => v && setActiveKey(v)}
                                        sx={appStyles.alphaNumberToggleGroupVertical}
                                    >
                                        {LEFT_1.map((k) => (
                                            <StyledAlphaFilterToggleButton
                                                key={k}
                                                value={k}
                                                sx={appStyles.alphaNumberToggleButton}
                                            >
                                                <span className="rv-pill">
                                                    <span className="rv-label">{k}</span>
                                                    <span className="rv-count">{counts[k] ?? 0}</span>
                                                </span>
                                            </StyledAlphaFilterToggleButton>
                                        ))}
                                    </ToggleButtonGroup>

                                    {/* N–Z */}
                                    <ToggleButtonGroup
                                        orientation="vertical"
                                        value={activeKey}
                                        exclusive
                                        onChange={(_, v) => v && setActiveKey(v)}
                                        sx={appStyles.alphaNumberToggleGroupVertical}
                                    >
                                        {LEFT_2.map((k) => (
                                            <StyledAlphaFilterToggleButton
                                                key={k}
                                                value={k}
                                                sx={appStyles.alphaNumberToggleButton}
                                            >
                                                <span className="rv-pill">
                                                    <span className="rv-label">{k}</span>
                                                    <span className="rv-count">{counts[k] ?? 0}</span>
                                                </span>
                                            </StyledAlphaFilterToggleButton>
                                        ))}
                                    </ToggleButtonGroup>
                                </Stack>

                                {/* Footer divider + spanning button */}
                                <Stack sx={appStyles.alphaNumberFooterContainer}>
                                    <ToggleButtonGroup
                                        orientation="horizontal"
                                        value={activeKey}
                                        exclusive
                                        onChange={(_, v) => v && setActiveKey(v)}
                                        sx={appStyles.alphaNumberToggleGroupSpan}
                                    >
                                        <StyledAlphaFilterToggleButton
                                            value={SPAN}
                                            sx={[
                                                appStyles.alphaNumberToggleButton,
                                                appStyles.alphaNumberToggleButtonSpan,
                                                appStyles.alphaNumberToggleButtonFooter,
                                            ]}
                                        >
                                            <span className="rv-pill">
                                                <span className="rv-label">{SPAN}</span>
                                                <span className="rv-count">{counts[SPAN] ?? 0}</span>
                                            </span>
                                        </StyledAlphaFilterToggleButton>
                                    </ToggleButtonGroup>
                                </Stack>
                            </Stack>
                        </Stack>

                        {/* CENTER: results panel grows */}
                        <Stack spacing={1} sx={appStyles.alphaNumberCenter}>
                            <Stack sx={appStyles.alphaNumberResultsPanel} direction="column">
                                {/* Panel section title — mirrors desktop datasetPanel.setTitle(plural) */}
                                <Typography sx={{...appStyles.panelSectionTitle, padding: "8px 0 12px"}}>
                                    {friendlyName}
                                </Typography>

                                {/* Row 1: desktop actions (Create / Enrich / Research), right-anchored */}
                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" flexWrap="wrap" sx={{width: "100%", padding: "0 12px 10px", rowGap: 1}}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<AddCircleOutlineIcon />}
                                        onClick={() => navigate(
                                            `/networks/${networkUID}/intel/${typeKey}/${defKey}/create`,
                                            { state: { profile: state?.profile } }
                                        )}
                                        sx={appStyles.panelActionButton}
                                    >
                                        Create
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => {
                                            // TODO: Replace with bulk on-demand enrichment API call (desktop: System.out.println stub)
                                            console.log("Running bulk on-demand enrichment process");
                                        }}
                                        sx={appStyles.panelActionButton}
                                    >
                                        Enrich
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => {
                                            // TODO: Replace with bulk on-demand research API call (desktop: System.out.println stub)
                                            console.log("Running bulk on-demand research process");
                                        }}
                                        sx={appStyles.panelActionButton}
                                    >
                                        Research
                                    </Button>
                                </Stack>

                                <Divider flexItem sx={{width: "100%", borderColor: "#3F464F", marginBottom: "10px"}} />

                                {/* Row 2: filter + count, centered above the results */}
                                <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{padding: "0 12px 6px"}}>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{width: 350}}>
                                        <TextField
                                            size="small"
                                            placeholder="Filter results"
                                            value={resultsFilter}
                                            onChange={(e) => setResultsFilter(e.target.value)}
                                            sx={appStyles.alphaNumberFilterField}
                                            fullWidth
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <img
                                                            src="/images/search_white.svg"
                                                            height={16}
                                                            style={{ opacity: 0.75 }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Stack>
                                    <Typography sx={appStyles.alphaNumberFilterCount}>
                                        {filteredCount} results
                                    </Typography>
                                </Stack>

                                {/* Result cards — wrap flow, centered in the panel (mirrors UXDiscussionSelect topic list) */}
                                {visible.length === 0 ? (
                                    <Typography sx={{color: "#a8b8c0", fontSize: "0.8rem", textAlign: "center", padding: "24px 0"}}>
                                        No profiles match the current filter.
                                    </Typography>
                                ) : (
                                    <Box sx={{display: "flex", flexWrap: "wrap", gap: "10px", justifyContent: "center", padding: "8px 16px 16px"}}>
                                        {visible.map((p) => (
                                            <PointerCard
                                                key={p.matchHash}
                                                pointer={p}
                                                friendlyType={friendlyType}
                                                friendlyDefinition={friendlyName}
                                                onSelect={() => navigate(
                                                    `/networks/${networkUID}/intel/${typeKey}/${defKey}/${p.matchHash}`,
                                                    { state: { profile: state?.profile } }
                                                )}
                                            />
                                        ))}
                                    </Box>
                                )}
                            </Stack>
                        </Stack>
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
}

// Mirrors UXDiscussionSelect TopicCard look: fixed-width card, gradient bg, cyan hover.
// Internal: icon in cyan-tinted square + stacked text (display, type, definition).
const pointerCardSx = {
    background: "linear-gradient(135deg, rgba(9,25,32,0.85) 0%, rgba(20,40,55,0.7) 100%)",
    border: "1px solid #3F464F",
    borderRadius: "12px",
    padding: "12px 16px",
    width: 360,
    minWidth: 300,
    cursor: "pointer",
    transition: "border-color 0.15s, background-color 0.15s",
    "&:hover": {
        borderColor: "rgba(2, 207, 238, 0.45)",
        backgroundColor: "rgba(2, 207, 238, 0.04)",
    },
};

function PointerCard({pointer, friendlyType, friendlyDefinition, onSelect}: {
    pointer: SamplePointer;
    friendlyType: string;
    friendlyDefinition: string;
    onSelect: () => void;
}) {
    const iconKey = (pointer.typeKey || "").toLowerCase();
    return (
        <Box sx={pointerCardSx} onClick={onSelect}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box sx={{
                    width: 40, height: 40, borderRadius: "8px", flexShrink: 0,
                    backgroundColor: "rgba(2, 207, 238, 0.12)",
                    border: "1px solid rgba(2, 207, 238, 0.25)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    overflow: "hidden",
                }}>
                    <Box
                        component="img"
                        src={`/images/intel/${iconKey}.svg`}
                        onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.src = "/images/intel/identifier.svg"; }}
                        sx={{width: 24, height: 24, opacity: 0.85}}
                    />
                </Box>
                <Stack spacing={0.3} sx={{minWidth: 0, flex: 1}}>
                    <Typography sx={{fontSize: "0.85rem", fontWeight: 600, color: "#F2F5FA", textAlign: "left"}}>
                        {pointer.display}
                    </Typography>
                    <Typography sx={{fontSize: "0.7rem", fontWeight: 300, color: "rgba(242, 245, 250, 0.7)", textAlign: "left"}}>
                        {`Intelligence: ${friendlyType}`}
                    </Typography>
                    <Typography sx={{fontSize: "0.6rem", fontWeight: 400, color: "#a8b8c0", textAlign: "left"}}>
                        {friendlyDefinition}
                    </Typography>
                </Stack>
            </Stack>
        </Box>
    );
}

export default UXIntelManager;
